import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaImage, FaDollarSign, FaShoppingCart, FaUsers, FaGift, FaTrash } from 'react-icons/fa';
import { format } from 'date-fns';
import ImageCard from './ImageCard';

const UserDetailView = ({ 
  selectedUser, 
  giftTokens, 
  setGiftTokens, 
  handleGiftTokens, 
  stripeData, 
  isLoadingStripe, 
  fetchStripeStatus, 
  selectedUserId, 
  handleImageClick, 
  navigateBack,
  getAccessTokenSilently,
  fetchUserDetails
}) => {
  const [deletePaymentDialog, setDeletePaymentDialog] = useState({ isOpen: false, payment: null, isDeleting: false });

  const handleDeletePayment = async (payment) => {
    if (!payment) return;

    try {
      setDeletePaymentDialog(prev => ({ ...prev, isDeleting: true }));
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      console.log('[RETRO-ADMIN] 🗑️ Deleting payment:', {
        paymentId: payment.id,
        amount: payment.amount,
        timestamp: new Date().toISOString()
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/stripe/charges/${payment.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete payment');
      }

      const result = await response.json();
      
      console.log('[RETRO-ADMIN] ✅ Payment deleted successfully:', result);
      
      // Close dialog
      setDeletePaymentDialog({ isOpen: false, payment: null, isDeleting: false });
      
      // Refresh user details and Stripe data
      if (selectedUserId) {
        await fetchUserDetails(selectedUserId);
        await fetchStripeStatus(selectedUserId);
      }
      
      // Show success message (you might want to add a toast notification here)
      console.log('[RETRO-ADMIN] 🎉 Payment refunded:', result.message);
      
    } catch (error) {
      console.error('[RETRO-ADMIN] ❌ Error deleting payment:', error);
      // You might want to show an error toast here
      setDeletePaymentDialog(prev => ({ ...prev, isDeleting: false }));
    }
  };
  if (!selectedUser) return null;

  return (
    <div className="space-y-8 max-w-[920px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={navigateBack}
            className="flex items-center gap-2 px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[18px] text-black/60 hover:text-black/80 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Users
          </motion.button>
          <div>
            <h1 className="text-2xl font-['VT323'] text-black/60">{selectedUser.email}</h1>
            <p className="text-sm font-['VT323'] text-black/40">
              User since {format(new Date(selectedUser.createdAt), 'MMMM d, yyyy')}
            </p>
          </div>
        </div>
      </div>

      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/90 backdrop-blur-xl rounded-xl p-4 shadow-sm border border-black/[0.08]">
          <div className="flex items-center gap-2">
            <FaImage className="w-4 h-4 text-blue-500" />
            <h3 className="font-['VT323'] text-lg text-black/40">Images</h3>
          </div>
          <p className="mt-1 text-xl font-['VT323'] text-black/60">
            {selectedUser.stats.totalImages}
          </p>
        </div>

        <div className="bg-white/90 backdrop-blur-xl rounded-xl p-4 shadow-sm border border-black/[0.08]">
          <div className="flex items-center gap-2">
            <FaDollarSign className="w-4 h-4 text-yellow-500" />
            <h3 className="font-['VT323'] text-lg text-black/40">Total Spent</h3>
          </div>
          <p className="mt-1 text-xl font-['VT323'] text-black/60">
            ${selectedUser.stats.totalSpent}
          </p>
        </div>

        <div className="bg-white/90 backdrop-blur-xl rounded-xl p-4 shadow-sm border border-black/[0.08]">
          <div className="flex items-center gap-2">
            <FaShoppingCart className="w-4 h-4 text-green-500" />
            <h3 className="font-['VT323'] text-lg text-black/40">Purchased Tokens</h3>
          </div>
          <p className="mt-1 text-xl font-['VT323'] text-black/60">
            {selectedUser.stats.totalPurchasedTokens || 0}
          </p>
          <p className="text-sm font-['VT323'] text-black/40">
            {selectedUser.stats.totalPurchases} purchases
          </p>
        </div>

        <div className="bg-white/90 backdrop-blur-xl rounded-xl p-4 shadow-sm border border-black/[0.08]">
          <div className="flex items-center gap-2">
            <FaUsers className="w-4 h-4 text-purple-500" />
            <h3 className="font-['VT323'] text-lg text-black/40">Current Balance</h3>
          </div>
          <p className="mt-1 text-xl font-['VT323'] text-black/60">
            {selectedUser.tokenBalance}
          </p>
          <p className="text-sm font-['VT323'] text-black/40">
            {selectedUser.stats.totalGiftedTokens || 0} gifted
          </p>
        </div>
      </div>

      {/* Gift Tokens Form */}
      <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
        <div className="flex items-center gap-2 mb-4">
          <FaGift className="w-5 h-5 text-purple-500" />
          <h2 className="font-['VT323'] text-xl text-black/40">Gift Tokens</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-['VT323'] text-black/60 mb-2">Amount</label>
            <input
              type="number"
              min="1"
              max="10000"
              value={giftTokens.amount}
              onChange={(e) => setGiftTokens(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="Number of tokens"
              className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-['VT323'] text-black/60 mb-2">Reason (Optional)</label>
            <input
              type="text"
              value={giftTokens.reason}
              onChange={(e) => setGiftTokens(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason for gift"
              className="w-full px-4 py-3 bg-white/90 text-black border border-black/10 rounded-xl font-['VT323'] text-[16px] focus:outline-none focus:ring-2 focus:ring-[#00FF9F]/50 focus:bg-white hover:bg-white transition-all duration-200"
            />
          </div>
          <div className="flex items-end">
            <motion.button
              onClick={handleGiftTokens}
              disabled={!giftTokens.amount || giftTokens.amount <= 0 || giftTokens.isGifting}
              className="w-full px-6 py-3 bg-[#00FF9F] hover:bg-[#00FF9F]/90 text-black font-['VT323'] text-[18px] rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {giftTokens.isGifting ? 'Gifting...' : 'Gift Tokens'}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Stripe Status Section */}
      <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FaDollarSign className="w-5 h-5 text-green-500" />
            <h2 className="font-['VT323'] text-xl text-black/40">Stripe Customer Status</h2>
          </div>
          <motion.button
            onClick={() => fetchStripeStatus(selectedUserId)}
            disabled={isLoadingStripe}
            className="px-4 py-2 bg-black/[0.03] hover:bg-black/[0.05] rounded-lg font-['VT323'] text-[16px] text-black/60 hover:text-black/80 transition-colors disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoadingStripe ? 'Loading...' : 'Refresh'}
          </motion.button>
        </div>

        {isLoadingStripe ? (
          <div className="flex items-center justify-center py-8">
            <motion.div 
              className="w-6 h-6 border-2 border-black/20 border-t-black/40 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
        ) : stripeData ? (
          <div className="space-y-6">
            
            {/* Customer Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-black/[0.02] p-4 rounded-lg">
                <h3 className="font-['VT323'] text-[16px] text-black/60 mb-2">Customer Status</h3>
                {stripeData.hasStripeCustomer ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-['VT323'] text-[14px] text-black/70">Active Customer</span>
                    </div>
                    <div className="text-xs font-['VT323'] text-black/50">
                      ID: {stripeData.customer?.id}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="font-['VT323'] text-[14px] text-black/70">No Stripe Customer</span>
                  </div>
                )}
              </div>

              <div className="bg-black/[0.02] p-4 rounded-lg">
                <h3 className="font-['VT323'] text-[16px] text-black/60 mb-2">Subscriptions</h3>
                <div className="font-['VT323'] text-[18px] text-black/70">
                  {stripeData.subscriptions?.length || 0}
                </div>
                <div className="text-xs font-['VT323'] text-black/50">
                  {stripeData.subscriptions?.filter(s => s.status === 'active').length || 0} active
                </div>
              </div>

              <div className="bg-black/[0.02] p-4 rounded-lg">
                <h3 className="font-['VT323'] text-[16px] text-black/60 mb-2">Total Payments</h3>
                <div className="font-['VT323'] text-[18px] text-black/70">
                  {stripeData.charges?.length || 0}
                </div>
                <div className="text-xs font-['VT323'] text-black/50">
                  ${((stripeData.charges || []).filter(c => c.status === 'succeeded').reduce((sum, c) => sum + c.amount, 0) / 100).toFixed(2)} total
                </div>
              </div>
            </div>

            {/* Error Display */}
            {stripeData.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-['VT323'] text-[16px] text-red-700 mb-2">Stripe API Error</h3>
                <div className="text-sm font-['VT323'] text-red-600">
                  {stripeData.error.message}
                </div>
                <div className="text-xs font-['VT323'] text-red-500 mt-1">
                  Type: {stripeData.error.type} | Code: {stripeData.error.code}
                </div>
              </div>
            )}

            {/* Customer Details */}
            {stripeData.customer && (
              <div className="bg-black/[0.02] p-4 rounded-lg">
                <h3 className="font-['VT323'] text-[16px] text-black/60 mb-3">Customer Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-['VT323'] text-black/50">Email:</span>
                    <span className="font-['VT323'] text-black/70 ml-2">{stripeData.customer.email || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="font-['VT323'] text-black/50">Created:</span>
                    <span className="font-['VT323'] text-black/70 ml-2">
                      {format(new Date(stripeData.customer.created * 1000), 'MMM d, yyyy')}
                    </span>
                  </div>
                  <div>
                    <span className="font-['VT323'] text-black/50">Default Payment:</span>
                    <span className="font-['VT323'] text-black/70 ml-2">
                      {stripeData.customer.default_source ? 'Set' : 'None'}
                    </span>
                  </div>
                  <div>
                    <span className="font-['VT323'] text-black/50">Delinquent:</span>
                    <span className="font-['VT323'] text-black/70 ml-2">
                      {stripeData.customer.delinquent ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Active Subscriptions */}
            {stripeData.subscriptions && stripeData.subscriptions.length > 0 && (
              <div className="bg-black/[0.02] p-4 rounded-lg">
                <h3 className="font-['VT323'] text-[16px] text-black/60 mb-3">Subscriptions</h3>
                <div className="space-y-3">
                  {stripeData.subscriptions.map(subscription => (
                    <div key={subscription.id} className="bg-white/50 p-3 rounded border border-black/5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-['VT323'] text-[14px] text-black/70">
                            {subscription.id}
                          </span>
                          <span className={`px-2 py-1 text-xs font-['VT323'] rounded ${
                            subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                            subscription.status === 'canceled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {subscription.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-xs font-['VT323'] text-black/50">
                          ${(subscription.items?.data[0]?.price?.unit_amount / 100).toFixed(2)}/{subscription.items?.data[0]?.price?.recurring?.interval}
                        </div>
                      </div>
                      <div className="text-xs font-['VT323'] text-black/50">
                        Current period: {format(new Date(subscription.current_period_start * 1000), 'MMM d')} - {format(new Date(subscription.current_period_end * 1000), 'MMM d, yyyy')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Payments */}
            {stripeData.charges && stripeData.charges.length > 0 && (
              <div className="bg-black/[0.02] p-4 rounded-lg">
                <h3 className="font-['VT323'] text-[16px] text-black/60 mb-3">Recent Payments</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {stripeData.charges.slice(0, 10).map(charge => (
                    <div key={charge.id} className="flex items-center justify-between py-2 border-b border-black/5 last:border-b-0">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-['VT323'] text-[14px] text-black/70">
                            ${(charge.amount / 100).toFixed(2)}
                          </span>
                          <span className={`px-2 py-1 text-xs font-['VT323'] rounded ${
                            charge.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                            charge.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {charge.status.toUpperCase()}
                          </span>
                          {charge.refunded && (
                            <span className="px-2 py-1 text-xs font-['VT323'] rounded bg-orange-100 text-orange-800">
                              REFUNDED
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-['VT323'] text-black/50">
                          {format(new Date(charge.created * 1000), 'MMM d, yyyy h:mm a')}
                        </div>
                        {charge.description && (
                          <div className="text-xs font-['VT323'] text-black/40">
                            {charge.description}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-xs font-['VT323'] text-black/40">
                          {charge.payment_method_details?.card?.brand?.toUpperCase()} ****{charge.payment_method_details?.card?.last4}
                        </div>
                        {charge.status === 'succeeded' && !charge.refunded && (
                          <motion.button
                            onClick={() => {
                              console.log('[RETRO-ADMIN] 🗑️ Delete button clicked:', {
                                chargeId: charge.id,
                                amount: charge.amount,
                                status: charge.status,
                                refunded: charge.refunded,
                                timestamp: new Date().toISOString()
                              });
                              setDeletePaymentDialog({ isOpen: true, payment: charge, isDeleting: false });
                            }}
                            className="p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Refund Payment"
                          >
                            <FaTrash className="w-3 h-3 text-red-500" />
                          </motion.button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Methods */}
            {stripeData.paymentMethods && stripeData.paymentMethods.length > 0 && (
              <div className="bg-black/[0.02] p-4 rounded-lg">
                <h3 className="font-['VT323'] text-[16px] text-black/60 mb-3">Payment Methods</h3>
                <div className="space-y-2">
                  {stripeData.paymentMethods.map(pm => (
                    <div key={pm.id} className="flex items-center justify-between p-2 bg-white/50 rounded">
                      <div className="flex items-center gap-2">
                        <span className="font-['VT323'] text-[14px] text-black/70">
                          {pm.card?.brand?.toUpperCase()} ****{pm.card?.last4}
                        </span>
                        <span className="text-xs font-['VT323'] text-black/50">
                          {pm.card?.exp_month}/{pm.card?.exp_year}
                        </span>
                      </div>
                      <div className="text-xs font-['VT323'] text-black/40">
                        {format(new Date(pm.created * 1000), 'MMM yyyy')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className="text-center py-8 text-black/40 font-['VT323']">
            Click "Refresh" to load Stripe customer data
          </div>
        )}
      </div>

      {/* Token Activity History */}
      {selectedUser.tokenActivities && selectedUser.tokenActivities.length > 0 && (
        <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
          <h2 className="font-['VT323'] text-xl text-black/40 mb-4">Token Activity History</h2>
          <div className="space-y-3">
            {selectedUser.tokenActivities.map(activity => (
              <div key={activity.id} className="flex items-center justify-between p-3 bg-black/[0.02] rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="font-['VT323'] text-[16px] text-black/70">
                      {activity.amount} tokens
                    </div>
                    {activity.isPurchase ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-['VT323'] rounded">
                        PURCHASE
                      </span>
                    ) : activity.isGift ? (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-['VT323'] rounded">
                        GIFT
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-['VT323'] rounded">
                        {activity.type?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-['VT323'] text-black/50">
                    {format(new Date(activity.createdAt), 'MMM d, yyyy h:mm a')}
                  </div>
                  {activity.reason && (
                    <div className="text-xs font-['VT323'] text-black/40 mt-1">
                      Reason: {activity.reason}
                    </div>
                  )}
                  {activity.giftedBy && (
                    <div className="text-xs font-['VT323'] text-black/40 mt-1">
                      Gifted by: {activity.giftedBy}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  {activity.isPurchase ? (
                    <div className="font-['VT323'] text-[16px] text-black/60">
                      ${activity.cost}
                    </div>
                  ) : (
                    <div className="font-['VT323'] text-[16px] text-purple-600">
                      FREE
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Images */}
      {selectedUser.images.length > 0 && (
        <div className="bg-white/90 backdrop-blur-xl rounded-xl p-6 shadow-sm border border-black/[0.08]">
          <h2 className="font-['VT323'] text-xl text-black/40 mb-4">Recent Images</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {selectedUser.images.slice(0, 24).map(image => (
              <ImageCard
                key={image.id}
                image={image}
                onClick={() => handleImageClick(image)}
                onImageLoaded={() => {}}
                isAnimated={true}
                showFavoriteCount={false}
                hideHoverButtons={false}
                isAdminView={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Delete Payment Dialog */}
      <AnimatePresence>
        {deletePaymentDialog.isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              onClick={() => !deletePaymentDialog.isDeleting && setDeletePaymentDialog({ isOpen: false, payment: null, isDeleting: false })}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-xl p-6 m-4 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="font-['VT323'] text-xl text-black/70 mb-4">Confirm Payment Refund</h3>
                
                {deletePaymentDialog.payment && (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="font-['VT323'] text-[16px] text-red-800 mb-2">
                        ⚠️ This will refund the payment and remove tokens
                      </div>
                      <div className="text-sm font-['VT323'] text-red-700">
                        This action will:
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Refund ${(deletePaymentDialog.payment.amount / 100).toFixed(2)} to the customer</li>
                          <li>Remove purchased tokens from their account</li>
                          <li>Delete the purchase record from your database</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-black/[0.02] p-4 rounded-lg">
                      <h4 className="font-['VT323'] text-[14px] text-black/60 mb-2">Payment Details</h4>
                      <div className="space-y-1 text-sm">
                        <div>
                          <span className="font-['VT323'] text-black/50">Amount:</span>
                          <span className="font-['VT323'] text-black/70 ml-2">
                            ${(deletePaymentDialog.payment.amount / 100).toFixed(2)}
                          </span>
                        </div>
                        <div>
                          <span className="font-['VT323'] text-black/50">Date:</span>
                          <span className="font-['VT323'] text-black/70 ml-2">
                            {format(new Date(deletePaymentDialog.payment.created * 1000), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                        <div>
                          <span className="font-['VT323'] text-black/50">Payment ID:</span>
                          <span className="font-['VT323'] text-black/70 ml-2 text-xs">
                            {deletePaymentDialog.payment.id}
                          </span>
                        </div>
                        {deletePaymentDialog.payment.description && (
                          <div>
                            <span className="font-['VT323'] text-black/50">Description:</span>
                            <span className="font-['VT323'] text-black/70 ml-2">
                              {deletePaymentDialog.payment.description}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <motion.button
                        onClick={() => setDeletePaymentDialog({ isOpen: false, payment: null, isDeleting: false })}
                        disabled={deletePaymentDialog.isDeleting}
                        className="flex-1 px-4 py-3 bg-black/[0.03] hover:bg-black/[0.05] text-black/60 font-['VT323'] text-[16px] rounded-lg transition-colors disabled:opacity-50"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        onClick={() => handleDeletePayment(deletePaymentDialog.payment)}
                        disabled={deletePaymentDialog.isDeleting}
                        className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-['VT323'] text-[16px] rounded-lg transition-colors disabled:opacity-50"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {deletePaymentDialog.isDeleting ? (
                          <div className="flex items-center justify-center gap-2">
                            <motion.div 
                              className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            />
                            Refunding...
                          </div>
                        ) : (
                          'Refund Payment'
                        )}
                      </motion.button>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserDetailView; 